import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Report, type Workload } from '@/api/client'
import { BAR, ErrorNote, Spinner, Tabs, fmtDay, monthStartISO, todayISO }
  from '@/components/ui'

// The firm's month, not the browser's - see `monthStartISO` in `ui`.
const first = () => monthStartISO()

/** Proportion bars over charts, still: every bar carries its numbers beside it and
 *  the status palette is the same one the rest of the app uses.
 *
 *  The trend is the one exception — "are we keeping up" is a question about
 *  direction, and no single number answers it. It is drawn as paired daily bars
 *  rather than a line, because the days are discrete and there are few of them.
 *
 *  **Every figure links to the tasks behind it.** A report you cannot act on sends
 *  you off to rebuild the same filter by hand on another screen, and most people
 *  do not bother — which is how a number nobody trusts survives for months. */
/** Five panels rather than eight, and more closely related - but tabbed for the same
 *  reason, and so the two report screens behave alike. `summary` keeps together the
 *  figures that explain each other: the four totals and the trend they came from. */
const TABS = [
  ['summary', 'Summary'],
  ['people', 'By person'],
  ['companies', 'By company'],
  ['approvals', 'Approvals'],
  ['ageing', 'Open & ageing'],
] as const
type Tab = typeof TABS[number][0]

export function Reports() {
  const [tab, setTab] = useState<Tab>('summary')
  const [from, setFrom] = useState(first())
  const [to, setTo] = useState(todayISO())
  const [saving, setSaving] = useState('')
  const [saveErr, setSaveErr] = useState<unknown>(null)

  const save = async (ext: 'xlsx' | 'csv') => {
    setSaveErr(null)
    setSaving(ext)
    try {
      await api.download(`/reports/export.${ext}?date_from=${from}&date_to=${to}`)
    } catch (e) {
      setSaveErr(e)
    } finally {
      setSaving('')
    }
  }
  const q = useQuery({
    queryKey: ['report', from, to],
    queryFn: () => api.get<Report>(`/reports/summary?date_from=${from}&date_to=${to}`),
  })

  // Its own query because it is its own endpoint: a snapshot of now, deliberately
  // not scoped to the range above it.
  const w = useQuery({
    queryKey: ['workload'],
    queryFn: () => api.get<Workload>('/reports/workload'),
  })

  if (q.isLoading) return <Spinner label="Crunching" />
  if (q.error) return <ErrorNote error={q.error} />
  const r = q.data!

  /** A link to the tasks a figure counted, carrying this page's range across so the
   *  list shows the same slice the number was computed from. */
  const drill = (extra: Record<string, string>) =>
    `/tasks?${new URLSearchParams({ range: 'custom', from, to, ...extra })}`

  const Row = ({ name, s, to: href }: {
    name: string; s: Report['by_person'][string]; to?: string
  }) => (
    <Link to={href ?? '#'}
          className="flex items-center gap-3 px-4 py-2 border-b border-rule-2
                     hover:bg-paper">
      <div className="w-36 shrink-0 text-[13px] truncate">{name}</div>
      <div className="flex-1">
        <div className="flex h-3.5 gap-[2px] rounded-sm overflow-hidden">
          {([['completed','done'],['pending','pend'],['rework','rej'],['open','open']] as const)
            .map(([k, kind]) => s[k] > 0 && (
              <i key={k} style={{ flex: s[k] }} className={BAR[kind]} />
            ))}
        </div>
      </div>
      <div className="w-16 shrink-0 mono text-right text-[12.5px] text-ink-2">{s.total}</div>
      <div className="w-12 shrink-0 mono text-right text-[12.5px] font-semibold text-done-ink">
        {s.rate}%
      </div>
    </Link>
  )

  const Panel = ({ title, data, kind }: {
    title: string; data: Report['by_person']; kind: 'person' | 'company'
  }) => (
    <div className="card">
      <div className="card-head">
        <b className="text-[13px]">{title}</b>
        <span className="flex-1" />
        <div className="flex gap-3.5">
          {([['Completed','done'],['Pending','pend'],['Rework','rej'],['Open','open']] as const)
            .map(([l, k]) => (
              <span key={l} className="flex items-center gap-1.5 text-[11.5px] text-ink-2">
                <i className={`h-2 w-2 rounded-sm ${BAR[k]}`} /> {l}
              </span>))}
        </div>
      </div>
      {Object.entries(data)
        .sort((a, b) => b[1].total - a[1].total)
        .map(([name, s]) => (
          <Row key={name} name={name} s={s}
               to={drill(kind === 'person'
                 ? { responsible: r.ids.person[name] ?? '' }
                 : { company: r.ids.company[name] ?? '' })} />
        ))}
      <div className="px-4 py-2 text-[11.8px] text-ink-3">
        Right-hand figure is the share completed. Click a row for those tasks.
      </div>
    </div>
  )

  return (
    <>
      {/* Wraps: at 390px a non-wrapping row pushed the export buttons off-screen. */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="label">From</label>
          <input type="date" className="field w-36 sm:w-40" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className="label">To</label>
          <input type="date" className="field w-36 sm:w-40" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <span className="hidden sm:block flex-1" />
        <button onClick={() => save('xlsx')} disabled={!!saving}
                className="h-9 px-3.5 rounded-md border border-rule bg-surface text-[13px]
                           font-medium hover:bg-paper disabled:opacity-60">
          {saving === 'xlsx' ? 'Preparing…' : 'Download Excel'}
        </button>
        <button onClick={() => save('csv')} disabled={!!saving}
                className="h-9 px-3.5 rounded-md border border-rule bg-surface text-[13px]
                           hover:bg-paper disabled:opacity-60">
          {saving === 'csv' ? 'Preparing…' : 'CSV'}
        </button>
      </div>

      {saveErr ? <div className="mb-4"><ErrorNote error={saveErr} /></div> : null}

      <Tabs tabs={TABS} active={tab} onPick={setTab} />

      {/* A range that ends today is right for a completion rate — counting tomorrow's
          unstarted work would understate the firm. But it silently hides the list you
          have just written for tomorrow, and "0 of 0" reads as "there is nothing"
          rather than "there is nothing here". So it says which it is. */}
      {tab === 'summary' && r.outside_range > 0 && (
        <div className="card mb-4 px-4 py-2.5 flex flex-wrap items-center gap-2
                        text-[12.3px]">
          <span className={r.totals.tasks === 0 ? 'text-pend-ink' : 'text-ink-2'}>
            <b>{r.outside_range}</b> task{r.outside_range === 1 ? '' : 's'} fall
            outside this range{r.totals.tasks === 0 && ' — which is why every figure below is zero'}.
          </span>
          <span className="flex-1" />
          <button className="btn btn-sm"
                  onClick={() => { setFrom('2000-01-01'); setTo('2100-01-01') }}>
            Include everything
          </button>
        </div>
      )}

      {tab === 'summary' && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-4">
        {[
          ['Completion rate', `${r.totals.completion_rate}%`, `${r.totals.completed} of ${r.totals.tasks}`],
          ['Tasks in range', String(r.totals.tasks), `${r.range.from} → ${r.range.to}`],
          ['People active', String(Object.keys(r.by_person).length), 'with at least one task'],
          ['Ageing tasks', String(r.ageing.length), 'carried at least once'],
        ].map(([l, v, s]) => (
          <div key={l} className="card p-4">
            <div className="label">{l}</div>
            <div className="mono text-3xl font-semibold tracking-tight">{v}</div>
            <div className="text-[11.8px] text-ink-3 mt-1">{s}</div>
          </div>
        ))}
      </div>
      )}

      {/* Direction, which no single number on this page can give. Paired daily bars
          rather than a line: the days are discrete and there are few of them. */}
      {tab === 'summary' && r.trend.some((d) => d.raised || d.completed) && (
        <div className="card mb-4">
          <div className="card-head">
            <b className="text-[13px]">Raised against completed</b>
            <span className="flex-1" />
            <span className="text-[11.8px] text-ink-3">
              Counted from each task&rsquo;s own date, so it adds up to the total above.
            </span>
          </div>
          <div className="table-wrap">
            <div className="flex items-end gap-[3px] px-4 py-4 min-w-[560px] lg:min-w-0
                            h-40">
              {r.trend.map((d) => {
                const peak = Math.max(...r.trend.map((x) => x.raised), 1)
                return (
                  <Link key={d.date} to={drill({ range: 'custom', from: d.date, to: d.date })}
                        title={`${d.date}: ${d.raised} raised, ${d.completed} completed`}
                        /* h-full matters: the bars below are sized in percent, and a
                           percentage height inside an auto-height flex item resolves
                           to nothing - which drew an empty chart. */
                        className="flex-1 h-full flex flex-col justify-end items-stretch
                                   gap-[2px] min-w-[6px] group">
                    <span className={`${BAR.open} block rounded-t-sm opacity-70
                                      group-hover:opacity-100`}
                          style={{ height: `${(d.raised / peak) * 90}%` }} />
                    <span className={`${BAR.done} block rounded-b-sm`}
                          style={{ height: `${(d.completed / peak) * 90}%` }} />
                  </Link>
                )
              })}
            </div>
          </div>
          <div className="px-4 py-2 flex items-center gap-3.5 text-[11.5px] text-ink-2
                          border-t border-rule">
            <span className="flex items-center gap-1.5">
              <i className={`h-2 w-2 rounded-sm ${BAR.open}`} /> raised
            </span>
            <span className="flex items-center gap-1.5">
              <i className={`h-2 w-2 rounded-sm ${BAR.done}`} /> completed
            </span>
            <span className="flex-1" />
            <span className="mono text-ink-3">
              {fmtDay(r.trend[0]?.date)} → {fmtDay(r.trend[r.trend.length - 1]?.date)}
            </span>
          </div>
        </div>
      )}

      {tab === 'people' && (
        <Panel title="By person" data={r.by_person} kind="person" />
      )}
      {tab === 'companies' && (
        <Panel title="By company" data={r.by_company} kind="company" />
      )}

      {tab === 'approvals' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Approvals never carry forward, so a queue that is not moving shows up
            nowhere else in the app. */}
        <div className="card">
          <div className="card-head">
            <b className="text-[13px]">Waiting on an approver</b>
            <span className="flex-1" />
            <span className="text-[11.8px] text-ink-3">from submitted to decided</span>
          </div>
          {Object.keys(r.approvals).length === 0 ? (
            <div className="px-4 py-3 text-[12.5px] text-ink-3">
              Nothing was submitted for approval in this range.
            </div>
          ) : Object.entries(r.approvals)
            .sort((a, b) => b[1].waiting_now - a[1].waiting_now
                         || b[1].avg_hours - a[1].avg_hours)
            .map(([name, s]) => (
              <Link key={name}
                    to={drill({ approver: r.ids.approver[name] ?? '',
                                status: 'PENDING_APPROVAL', range: 'all' })}
                    className="flex items-center gap-3 px-4 py-2 border-b border-rule-2
                               hover:bg-paper">
                <div className="w-36 shrink-0 text-[13px] truncate">{name}</div>
                <div className="flex-1 mono text-[12.5px] text-ink-2">
                  {s.decided > 0
                    ? <>avg <b className="text-ink">{s.avg_hours}h</b>
                        <span className="text-ink-3"> · worst {s.worst_hours}h</span></>
                    : <span className="text-ink-3">nothing decided yet</span>}
                </div>
                <div className={`w-28 shrink-0 mono text-right text-[12.5px] ${
                  s.waiting_now ? 'text-pend-ink font-semibold' : 'text-ink-3'}`}>
                  {s.waiting_now} waiting
                </div>
              </Link>
            ))}
        </div>

        {/* What completion rate cannot say. */}
        <div className="card">
          <div className="card-head">
            <b className="text-[13px]">On time, and rework</b>
            <span className="flex-1" />
            <span className="text-[11.8px] text-ink-3">
              on time = finished without ever being carried
            </span>
          </div>
          {Object.entries(r.quality)
            .sort((a, b) => b[1].total - a[1].total)
            .map(([name, s]) => (
              <Link key={name} to={drill({ responsible: r.ids.person[name] ?? '' })}
                    className="flex items-center gap-3 px-4 py-2 border-b border-rule-2
                               hover:bg-paper">
                <div className="w-36 shrink-0 text-[13px] truncate">{name}</div>
                <div className="flex-1">
                  <div className="flex h-3.5 gap-[2px] rounded-sm overflow-hidden">
                    <i style={{ flex: Math.max(s.on_time_rate, 0.5) }} className={BAR.done} />
                    <i style={{ flex: Math.max(100 - s.on_time_rate, 0.5) }}
                       className="bg-rule-2" />
                  </div>
                </div>
                <div className="w-16 shrink-0 mono text-right text-[12.5px] font-semibold
                                text-done-ink">
                  {s.on_time_rate}%
                </div>
                <div className={`w-20 shrink-0 mono text-right text-[12.5px] ${
                  s.rework_rate > 0 ? 'text-rej-ink' : 'text-ink-3'}`}>
                  {s.rework_rate}% back
                </div>
              </Link>
            ))}
          <div className="px-4 py-2 text-[11.8px] text-ink-3">
            Rework is read from the history, so a task rejected and later finished
            still counts.
          </div>
        </div>
      </div>
      )}

      {/* A snapshot, and labelled as one: it does not move when the range does. */}
      {tab === 'ageing' && w.data && (
        <div className="card mt-4">
          <div className="card-head">
            <b className="text-[13px]">Open work right now</b>
            <span className="flex-1" />
            <span className="text-[11.8px] text-ink-3">
              As of {fmtDay(w.data.as_of)} — not affected by the range above
            </span>
          </div>
          {w.data.total === 0 ? (
            <div className="px-4 py-3 text-[12.5px] text-ink-3">
              Nothing open. Everything is finished or with an approver.
            </div>
          ) : Object.entries(w.data.by_person)
            .sort((a, b) => b[1].open - a[1].open)
            .map(([id, s]) => (
              // This map is keyed by user id now, so the drill-in no longer has to
              // look the id back up from a name - and the name it prints comes from
              // the entry rather than from the key.
              <Link key={id}
                    to={`/tasks?${new URLSearchParams({
                      range: 'all', status: 'OPEN',
                      responsible: id === '(unassigned)' ? '' : id })}`}
                    className="flex items-center gap-3 px-4 py-2 border-b border-rule-2
                               hover:bg-paper">
                <div className="w-36 shrink-0 text-[13px] truncate">{s.name}</div>
                <div className="flex-1">
                  <div className="flex h-3.5 gap-[2px] rounded-sm overflow-hidden">
                    {s.urgent > 0 && <i style={{ flex: s.urgent }} className={BAR.rej} />}
                    {s.important > 0 && <i style={{ flex: s.important }} className={BAR.pend} />}
                    {s.regular > 0 && <i style={{ flex: s.regular }} className={BAR.open} />}
                  </div>
                </div>
                <div className="w-16 shrink-0 mono text-right text-[12.5px]">{s.open}</div>
                <div className={`w-24 shrink-0 mono text-right text-[12.5px] ${
                  s.urgent ? 'text-rej-ink font-semibold' : 'text-ink-3'}`}>
                  {s.urgent} urgent
                </div>
              </Link>
            ))}
        </div>
      )}

      {tab === 'ageing' && r.ageing.length > 0 && (
        <div className="card mt-4">
          <div className="card-head">
            <b className="text-[13px]">Ageing — tasks that keep moving</b>
            <span className="flex-1" />
            <span className="text-[11.8px] text-ink-3">
              Flagged, never auto-cancelled — dropping a task is a person's call.
            </span>
          </div>
          {r.ageing.map((a) => (
            <div key={a.ref} className="flex items-center gap-3 px-4 py-2 border-b border-rule-2">
              <span className="mono w-14 text-primary text-[12.5px]">#{a.ref}</span>
              <span className="flex-1 text-[13px]">
                {a.task}
                <span className="block text-[11.7px] text-ink-3">{a.company} · {a.responsible}</span>
              </span>
              <div className="w-48">
                <div className="h-3 rounded-sm overflow-hidden bg-rule-2">
                  <i className={`block h-full ${a.carried >= 3 ? BAR.rej : a.carried >= 2 ? BAR.pend : BAR.open}`}
                     style={{ width: `${Math.min(a.carried / 5 * 100, 100)}%` }} />
                </div>
              </div>
              <span className="mono w-24 text-right text-[12.5px]">carried {a.carried}×</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
